import { createContext } from '@hover/blueprint';
import type {
  EmailMessage,
  LoadedMessage,
} from '@hover/embed/post-message-api';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useAiEmbeddedDesignerConfigurationQuery,
  useAuthenticationInfoLazyQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useQueryParam } from 'src/shared/hooks/useQueryParam';
import type { CreateLeadResponse } from 'src/shared/requests/createLead';

export interface EmbedContext {
  isEmbed: boolean;
  embedOrgId?: number;
  embedConfigId?: number;
  originalImageId?: number;
  leadData: CreateLeadResponse | null;
  cta?: string;
  setLeadData: (value: CreateLeadResponse) => void;
  showLandingPage?: boolean;
  ctaLink?: string;
  leadFormHeader?: string;
  leadFormSubheader?: string;
  hasLeadForm: boolean;
  hasLeadFormFullName: boolean;
  hasLeadFormPhoneNumber: boolean;
  hasLeadFormPostalCode: boolean;
  sendHomeownerEmail: boolean;
  header: string;
  subheader: string;
  userEmail?: string;
  embeddedAt?: string;
  hasAccount: boolean;
}

const defaultValue: EmbedContext = {
  isEmbed: false,
  showLandingPage: undefined,
  originalImageId: undefined,
  leadData: null,
  cta: undefined,
  ctaLink: undefined,
  leadFormHeader: undefined,
  leadFormSubheader: undefined,
  hasLeadForm: true,
  hasLeadFormFullName: true,
  hasLeadFormPhoneNumber: true,
  hasLeadFormPostalCode: true,
  sendHomeownerEmail: true,
  hasAccount: false,
  header: '',
  subheader: '',
  setLeadData: () => {},
};

const [Provider, useContext] = createContext<EmbedContext | undefined>({
  strict: false,
});

const getAllowedOriginsRegex = () => {
  if (__DEV__) {
    return [/^http:\/\/localhost:5173$/];
  }
  return [
    /^https:\/\/embed\.not-hover\.dev$/,
    /^https:\/\/www\.realtor\.com$/,
    /^https:\/\/.*\.realtor\.com$/,
    /^http:\/\/dev\.realtor\.com$/,
    /^http:\/\/dev\.realtor\.com:3000$/,
    /^https:\/\/.*\.previews\.hover\.to$/,
  ];
};

export interface EmbedProviderProps extends PropsWithChildren<{}> {}

export const EmbedProvider = (props: EmbedProviderProps) => {
  const hasEmbedQueryParam = useQueryParam('isEmbed') === 'true';
  const publicIdentifier = useQueryParam('aff_org_id');
  // If an originalImageId is passed into the embed, that should override the configuations
  const customOriginalImageId = useQueryParam('originalImageId') || undefined;
  const embedConfigurationId = useQueryParam('embedConfigId');
  const embeddedAtParam = useQueryParam('embeddedAt', true);

  const [isEmbed] = useState(() => hasEmbedQueryParam);
  const [embedOrgId, setEmbedOrgId] = useState<number | undefined>(undefined);
  const [leadData, setLeadData] = useState<CreateLeadResponse | null>(null);
  const [embedConfigId] = useState<number | undefined>(() =>
    Number(embedConfigurationId),
  );
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [embeddedAt] = useState<string | undefined>(() => embeddedAtParam);
  const [hasAccount, setHasAccount] = useState<boolean>(false);
  const [queryAuthenticationInfo] = useAuthenticationInfoLazyQuery({
    fetchPolicy: 'network-only',
  });

  const { setAffOrgPublicId } = useGlobalStorageContext();

  usePublicOrgInfoQuery({
    variables: {
      publicIdentifier: publicIdentifier || '',
    },
    skip: !publicIdentifier,
    onCompleted: data => {
      if (data?.publicOrgInfo?.id) {
        setEmbedOrgId(data.publicOrgInfo.id);
      }

      if (publicIdentifier) {
        setAffOrgPublicId(publicIdentifier);
      }
    },
  });

  const { data } = useAiEmbeddedDesignerConfigurationQuery({
    variables: { id: embedConfigId ? `${embedConfigId}` : '' },
    skip: !embedConfigId,
  });

  useEffect(() => {
    if (!isEmbed) return () => {};

    const checkAccountStatus = async (email: string) => {
      const authenticationInfo = await queryAuthenticationInfo({
        variables: { email },
      });
      setHasAccount(!!authenticationInfo.data?.authenticationInfo?.emailInUse);
    };

    const handleUserEmailMessage = (event: MessageEvent<EmailMessage>) => {
      if (getAllowedOriginsRegex().some(regex => regex.test(event.origin))) {
        if (event.data.type === 'email' && event.data.payload?.email) {
          const { email } = event.data.payload;
          setUserEmail(email);
          void checkAccountStatus(email);
        }
      }
    };

    window.addEventListener('message', handleUserEmailMessage);

    const loadedMessage: LoadedMessage = { type: 'loaded' };

    window.parent.postMessage(loadedMessage, '*');

    return () => {
      window.removeEventListener('message', handleUserEmailMessage);
    };
  }, [isEmbed, queryAuthenticationInfo]);

  const value = useMemo(() => {
    const {
      ctaLink,
      sampleImageIds,
      cta,
      hasLeadForm,
      hasLandingPage,
      hasLeadFormFullName,
      hasLeadFormPhoneNumber,
      hasLeadFormPostalCode,
      leadFormHeader,
      leadFormSubheader,
      sendHomeownerEmail,
      header,
      subheader,
    } = data?.aiEmbeddedDesignerConfigurations || {};

    // MEOW-5614 - handle multiple images, not just one sample image
    const originalImageId = customOriginalImageId
      ? Number(customOriginalImageId)
      : sampleImageIds?.[0];

    return {
      isEmbed,
      leadData,
      embedOrgId,
      embedConfigId,
      setLeadData,
      ctaLink: ctaLink ?? undefined,
      originalImageId,
      cta,
      leadFormHeader,
      leadFormSubheader,
      header: header ?? '',
      subheader: subheader ?? '',
      hasLeadForm: hasLeadForm ?? true,
      hasLeadFormFullName: hasLeadFormFullName ?? true,
      hasLeadFormPhoneNumber: hasLeadFormPhoneNumber ?? true,
      hasLeadFormPostalCode: hasLeadFormPostalCode ?? true,
      showLandingPage: hasLandingPage,
      sendHomeownerEmail: sendHomeownerEmail ?? true,
      userEmail,
      embeddedAt,
      hasAccount,
    };
  }, [
    data?.aiEmbeddedDesignerConfigurations,
    customOriginalImageId,
    isEmbed,
    leadData,
    embedOrgId,
    embedConfigId,
    userEmail,
    embeddedAt,
    hasAccount,
  ]);

  return <Provider value={value} {...props} />;
};

export const useEmbedContext = () => useContext() ?? defaultValue;
